/**
 * 수강후기 — **화면과 서버가 함께 쓰는 것**만 여기 둔다(타입·순수 함수).
 *
 * API 헬퍼(`reviews.ts`)는 `next/headers` 를 쓰는 서버 전용 모듈을 물고 있어서,
 * 클라이언트 컴포넌트가 거기서 타입 하나만 가져와도 **서버 모듈이 통째로 딸려 들어간다**
 * (실측 500 — `You're importing a module that depends on "next/headers"`).
 * `site-shared.ts` 와 같은 갈래다.
 */
export interface ReviewRow {
  id: number;
  subject: string | null;
  /** 본문 HTML — 목록에서는 글자만 뽑아 두 줄로 자른다(`plainText`) */
  content: string | null;
  starRating: number | null;
  productId: number | null;
  /** 강좌 이름 — 서버가 함께 준다(번호만 주면 화면이 상품을 다시 물어야 한다) */
  productName: string | null;
  writerName: string | null;
  viewCount: number;
  createdAt: string;
  /** 베스트 — 관리자가 올린 공지다(2026-09-05 사용자 확정, 별도 칸을 만들지 않는다) */
  isNotice: boolean;
  /** 사진 — 첫 장이 앨범형 썸네일, 보기 화면은 전부 그린다. 없으면 빈 배열 */
  attachments: { url: string; name: string; size: number }[];
  /** 단건에만 실린다 — 고치기는 본인·관리자, 지우기는 게시판의 삭제 허용까지 본다(서버가 정한다) */
  canEdit?: boolean;
  canDelete?: boolean;
  /** 단건에만 — 댓글(게시판 설정이 켜졌을 때). 게시글 댓글과 같은 모양이다 */
  commentEnabled?: boolean;
  canComment?: boolean;
  comments?: {
    id: number; writer: string | null; name: string | null; comment: string | null;
    createdAt: string; canEdit: boolean; canDelete: boolean;
  }[];
}

/** 후기 게시판 설정 — 화면이 UI 를 구성한다(서버가 판정해서 준다) */
export interface ReviewSetting {
  id: number;
  name: Record<string, string> | null;
  pageSize: number;
  editorEnabled: boolean;
  uploadCount: number | null;
  uploadSize: number | null;
  commentEnabled: boolean;
  canWrite: boolean;
  canComment: boolean;
  canNotice: boolean;
}

export interface ReviewList {
  rows: ReviewRow[];
  /** 베스트 — 맨 위 띠에 태운다. 목록(rows)에는 들어 있지 않다 */
  best: ReviewRow[];
  total: number;
  page: number;
  size: number;
}

export interface ReviewStats {
  avg: number;
  count: number;
  dist: { r5: number; r4: number; r3: number; r2: number; r1: number };
}

/**
 * 후기를 쓸 수 있는 강좌 한 줄 — **아직 안 쓴 것만** 온다(2026-09-05).
 * 이미 쓴 강좌는 서버가 빼고 준다. 고치는 길은 보기 화면의 [수정]이다.
 */
export interface WritableCourse {
  productId: number;
  name: string;
  type: string;
}

export type ReviewSort = 'new' | 'high' | 'low';

/**
 * 태그가 없는 **평문**인가 — 게시판이 에디터를 껐을 때 쓴 글이 그렇다.
 * 그런 글은 줄바꿈이 `\n` 이라 보기 화면이 `pre-wrap` 으로 그려야 줄이 산다.
 */
export function isPlain(html: string | null | undefined): boolean {
  return !!html && !/<[a-z!/][^>]*>/i.test(html);
}

/**
 * 본문을 **입력칸에 되돌릴 때** — 줄바꿈을 지키고 태그만 걷는다(수정 화면이 쓴다).
 * 목록용 `plainText` 는 한 줄로 뭉치므로(카드가 두 줄로 자른다) 그것을 쓰면 **줄이 사라진다**.
 */
export function plainMultiline(html: string | null): string {
  return (html ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * 본문에서 글자만 — 목록은 두 줄로 자른 **미리보기**를 그린다(가이드 `rv-card-content`가
 * `-webkit-line-clamp: 2`). 태그가 섞여 들어가면 두 줄이 아니라 태그가 보인다.
 * 자르지 않고 CSS 에 맡긴다 — 언어마다 글자 폭이 달라 글자 수로 자르면 줄이 어긋난다.
 */
export function plainText(html: string | null): string {
  return (html ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}
